import { createSelector } from 'reselect';
import {
  QUICKBOOKS_ONLINE_CONNECTION_STATUS_NAMESPACE,
  CONNECTION_STATUSES_QUICKBOOKS_ONLINE,
  QUICKBOOKS_ONLINE_ACCOUNT_DETAILS_NAMESPACE,
  QUICKBOOKS_ONLINE_LINKED_DATA_SUMMARY_NAMESPACE,
  PROCESS_KINDS_QUICKBOOKS_ONLINE
} from 'components/QuickbooksOnline/Data/Constants';
import { processInProgress } from 'components/QuickbooksOnline/Data/Data';
import QuickbooksApi from '../api/QuickbooksApi';
import { EmptyObject, EmptyArray } from 'resources/EmptyDefaults';
import QuickbooksOnlineCompanyApi from '../../../Resources/QuickbooksOnline/Company/Api'
import { denormalize } from 'normalizr';
import { getEntities } from 'resources/EntityData';
import { ENTITY_QBO_PROCESSES_LIST, qboProcessesListSchema } from 'schema/QuickbooksOnline/ProcessSchema';
import { ENTITY_QBO_IMPORT_PROCESSES_LIST, qboImportProcessesListSchema } from 'schema/QuickbooksOnline/ImportProcessSchema'
import { isPresent } from 'utils/isPresent';

export const getQuickbooksAccountDetails = createSelector(
  (state) => state[QUICKBOOKS_ONLINE_ACCOUNT_DETAILS_NAMESPACE],
  (quickbooksAccountDetails) => quickbooksAccountDetails || EmptyObject,
);

export const getQuickbooksLinkedDataSummary = createSelector(
  (state) => state[QUICKBOOKS_ONLINE_LINKED_DATA_SUMMARY_NAMESPACE],
  (quickbooksLinkedDataSummary) => quickbooksLinkedDataSummary || EmptyObject,
);

export const getQuickbooksProcessesPaginationData = createSelector(
  getEntities,
  (entities) => entities[ENTITY_QBO_PROCESSES_LIST]?.list || EmptyObject,
);

export const getQuickbooksImportProcessesPaginationData = createSelector(
  getEntities,
  (entities) => entities[ENTITY_QBO_IMPORT_PROCESSES_LIST]?.list || EmptyObject,
);

const getQuickbooksProcesses = createSelector(
  getEntities,
  (entities) => {
    if (entities[ENTITY_QBO_PROCESSES_LIST]?.list) {
      return denormalize('list', qboProcessesListSchema, entities).data
    }
    return EmptyArray;
  }
);

const getQuickbooksImportProcesses = createSelector(
  getEntities,
  (entities) => {
    if (entities[ENTITY_QBO_IMPORT_PROCESSES_LIST]?.list) {
      return denormalize('list', qboImportProcessesListSchema, entities).data
    }
    return EmptyArray;
  }
);

export const getQuickbooksLatestProcess = createSelector(
  getQuickbooksProcesses,
  (quickbooksProcesses) => {
    const sortedProcesses = quickbooksProcesses.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedProcesses[0] || EmptyObject;
  },
);

export const getQuickbooksLatestImport = createSelector(
  getQuickbooksProcesses,
  (quickbooksProcesses) => {
    const quickbooksImports = quickbooksProcesses.filter((process) => (
      process.kind === PROCESS_KINDS_QUICKBOOKS_ONLINE.import
    ))
    const sortedImports = quickbooksImports.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedImports[0] || EmptyObject;
  },
);

export const getIsQuickbooksProcessRunning = createSelector(
  getQuickbooksLatestProcess,
  ({ status }) => processInProgress(status),
);

const getQuickbooksConnectionStatus = createSelector(
  (state) => state[QUICKBOOKS_ONLINE_CONNECTION_STATUS_NAMESPACE],
  (quickbooksConnectionStatus) => quickbooksConnectionStatus || EmptyObject,
);

export const getQboLastConnected = createSelector(
  getQuickbooksConnectionStatus,
  (quickbooksConnectionStatus) => quickbooksConnectionStatus.last_connected_at
);

export const getQboCurrentStatus = createSelector(
  getQuickbooksConnectionStatus,
  (quickbooksConnectionStatus) => quickbooksConnectionStatus.current_status
)

export const getIsQuickbooksConnectionFetched = createSelector(
  getQuickbooksConnectionStatus,
  (quickbooksConnectionStatus) => isPresent(quickbooksConnectionStatus)
)

export const getIsQuickbooksConnectionActive = createSelector(
  getQboCurrentStatus,
  (connectionStatus) => connectionStatus === CONNECTION_STATUSES_QUICKBOOKS_ONLINE.active
)

export const fetchQuickbooksConnectionStatus = {
  fetchingApiFunc: QuickbooksApi.fetchQuickbooksConnectionStatus,
  selector: getQuickbooksConnectionStatus,
  paramsFromProps: () => [],
  dataNamespace: QUICKBOOKS_ONLINE_CONNECTION_STATUS_NAMESPACE,
  alwaysFetch: true,
};

export const fetchQuickbooksAccountDetails = {
  fetchingApiFunc: QuickbooksOnlineCompanyApi.show,
  selector: getQuickbooksAccountDetails,
  paramsFromProps: () => [],
  dataNamespace: QUICKBOOKS_ONLINE_ACCOUNT_DETAILS_NAMESPACE,
};

export const fetchQuickbooksLinkedDataSummary = {
  fetchingApiFunc: QuickbooksApi.fetchQBOLinkedDataSummary,
  selector: getQuickbooksLinkedDataSummary,
  paramsFromProps: () => [],
  dataNamespace: QUICKBOOKS_ONLINE_LINKED_DATA_SUMMARY_NAMESPACE,
};

export const fetchQuickbooksProcesses = {
  fetchingApiFunc: QuickbooksApi.fetchQBOProcesses,
  selector: getQuickbooksProcesses,
  paramsFromProps: (props) => [props],
  schema: qboProcessesListSchema,
  alwaysFetch: true,
};

export const fetchQuickbooksImportProcesses = {
  fetchingApiFunc: QuickbooksApi.fetchQBOProcesses,
  selector: getQuickbooksImportProcesses,
  paramsFromProps: (props) => [
    {
      ...props,
      filters: {
        kind: [PROCESS_KINDS_QUICKBOOKS_ONLINE.import]
      }
    }
  ],
  schema: qboImportProcessesListSchema,
  alwaysFetch: true,
};
